import type { FC, FormEvent } from 'react'

import { useId, useState } from 'react'
import { ArrowRight, Check, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

interface NewsletterFormProps {
    className?: string
    dark?: boolean
}

type FormState = 'idle' | 'sending' | 'done' | 'error'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export const NewsletterForm: FC<NewsletterFormProps> = ({ className, dark = false }) => {
    const id = useId()
    const [email, setEmail] = useState('')
    const [website, setWebsite] = useState('')
    const [state, setState] = useState<FormState>('idle')
    const [message, setMessage] = useState('')

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (!EMAIL_PATTERN.test(email.trim())) {
            setState('error')
            setMessage('لطفاً نشانی ایمیل را کامل بنویسید؛ مثل name@example.com')

            return
        }

        setState('sending')
        setMessage('')

        try {
            const response = await fetch('/api/newsletter', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: email.trim(), website })
            })

            const payload = await response.json().catch(() => null) as { message?: string } | null

            if (!response.ok) throw new Error(payload?.message || 'عضویت انجام نشد.')

            setState('done')
            setMessage('عضو خبرنامه شدید. خبر پخت‌های هفته هر پنجشنبه به دستتان می‌رسد.')
            setEmail('')
        }
        catch (error) {
            setState('error')
            setMessage(error instanceof Error && error.message ? error.message : 'عضویت انجام نشد. لطفاً چند لحظه‌ی دیگر دوباره امتحان کنید.')
        }
    }

    return (
        <form className={cn('max-w-md', className)} onSubmit={onSubmit} noValidate>
            <label htmlFor={`${id}-email`} className='font-heading text-2xl leading-snug'>
                خبرنامه‌ی هفتگی پخت
            </label>

            <p id={`${id}-hint`} className={cn('mt-2 text-sm leading-relaxed', dark ? 'text-background/60' : 'text-crust-soft')}>
                هر پنجشنبه یک ایمیل کوتاه: پخت آخر هفته، شیرینی‌های تازه و ساعات کاری روزهای تعطیل.
            </p>

            <div className={cn('mt-5 flex items-stretch border-b transition-colors duration-300', dark ? 'border-background/40 focus-within:border-background' : 'border-foreground/60 focus-within:border-foreground')}>
                <input
                    id={`${id}-email`}
                    type='email'
                    name='email'
                    autoComplete='email'
                    inputMode='email'
                    placeholder='نشانی ایمیل شما'
                    value={email}
                    onChange={event => {
                        setEmail(event.target.value)

                        if (state === 'error') setState('idle')
                    }}
                    aria-describedby={`${id}-hint ${id}-status`}
                    aria-invalid={state === 'error' ? true : undefined}
                    className={cn('h-12 min-w-0 flex-1 bg-transparent text-[0.9375rem] focus:outline-none', dark ? 'placeholder:text-background/45' : 'placeholder:text-crust-soft/80')}
                />

                <div className='sr-only' aria-hidden='true'>
                    <label htmlFor={`${id}-website`}>این فیلد را خالی بگذارید</label>
                    <input id={`${id}-website`} type='text' name='website' tabIndex={-1} autoComplete='off' value={website} onChange={event => setWebsite(event.target.value)} />
                </div>

                <button
                    type='submit'
                    disabled={state === 'sending'}
                    className='group inline-flex h-12 cursor-pointer items-center gap-2 ps-4 text-sm font-medium focus-visible:outline-2 focus-visible:outline-amber disabled:opacity-60'
                >
                    {state === 'sending' ? 'در حال ثبت‌نام' : 'عضویت'}

                    {state === 'sending'
                        ? <LoaderCircle className='size-4 animate-spin' aria-hidden='true' />
                        : <ArrowRight className='size-4 transition-transform duration-500 ease-(--ease-soft) rtl:-scale-x-100 group-hover:translate-x-1 rtl:group-hover:-translate-x-1' aria-hidden='true' />}
                </button>
            </div>

            <p id={`${id}-status`} role='status' className={cn('mt-3 min-h-5 text-sm', state === 'error' ? (dark ? 'text-[#f0a597]' : 'text-destructive') : (dark ? 'text-background/70' : 'text-crust-soft'))}>
                {state === 'done' && <Check className='me-1.5 inline size-4 align-[-3px] text-amber' aria-hidden='true' />}
                {message}
            </p>
        </form>
    )
}
